import * as THREE from "three";
import Representation2d from "../representation2d/Representation2d";
import ShapeTypeEnum from "../representation2d/ShapeTypeEnum";
import { ElementTypeEnum } from "../../elementTypeMappings";
import ProductData from "../productData/ProductData";
import CoveringData from "../productData/CoveringData";
import IfcCoveringTypeEnum from "../../enums/IfcCoveringTypeEnum";
import ShapeGeometry2d from "../geometry/ShapeGeometry2d";
import { getLayerForProductRepresentation2d } from "../../utils/layerUtils";
import ShapeRepresentation2d from "../representation2d/ShapeRepresentation2d";
import LineloopsGeometry2d from "../geometry/LineloopsGeometry2d";
import LineloopsRepresentation2d from "../representation2d/LineloopsRepresentation2d";
import SvgAsyncGeometry from "../geometry/SvgAsyncGeometry";
import SvgRepresentation2d from "../representation2d/SvgRepresentation2d";
import MaterialPair from "../material/MaterialPair";
import SvgMaterial from "../material/SvgMaterial";
import IProduct from "../../interfaces/IProduct";

const needsOutline = (data : ProductData) => {

    switch (data.productType)
    {
        case ElementTypeEnum.Fixture:
            return true;
        case ElementTypeEnum.Covering:
            const coveringData = data as CoveringData
            return coveringData.coveringType === IfcCoveringTypeEnum.CLADDING || coveringData.isSkirting;
        
        default: 
            return false;
    }
};

const createProductRepresentation2d = ( representation2d : Representation2d, 
                                        product: IProduct, 
                                        materialPair: MaterialPair)
 : THREE.Object3D | null => {
    
    const productData = product.productData;

    const hasOutline = needsOutline(productData);
    const layer = getLayerForProductRepresentation2d(productData.productType, representation2d.shapeType, productData);
    
    const topHeight = product.productData.productType == ElementTypeEnum.Wall ? product.getTopHeight() : 0;

    switch (representation2d.shapeType) {
    case ShapeTypeEnum.Shape:

        const shapeRepr = representation2d as ShapeRepresentation2d;

        let material = materialPair.getThreeJsMaterial();

        // Reverse outer profile for ceilings
        if (product.productData.productType === ElementTypeEnum.Covering) {
            const coveringData = product.productData as CoveringData;
            if (coveringData.coveringType === IfcCoveringTypeEnum.CEILING) {
                material = materialPair.getThreeJsMaterial().clone();
                material.side = THREE.BackSide;
                material.needsUpdate = true;
            }
        }

        return ShapeGeometry2d.create(  shapeRepr,
                                        material, 
                                        layer,
                                        new THREE.Vector3(0,0,topHeight),
                                        hasOutline);

    case ShapeTypeEnum.Lineloops:
        return LineloopsGeometry2d.create(  representation2d as LineloopsRepresentation2d, 
                                            layer, 
                                            new THREE.Vector3);

    case ShapeTypeEnum.SvgSymbol:
        const svgRepr = representation2d as SvgRepresentation2d;
        const svgMaterial = materialPair.materialData as SvgMaterial;
        
        if (!svgMaterial.url) {
            return null;
        }

        const geom = new SvgAsyncGeometry(  svgRepr, 
                                            svgMaterial.url,
                                            layer, 
                                            new THREE.Vector3(0,0, product.dimensions?.z ?? 0 ), 
                                            svgRepr.width, 
                                            svgRepr.height); 
        return geom.group;

    default:
        return null;
    }

};

export { createProductRepresentation2d };

